import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import EmptyState from "@/components/base/EmptyState";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { usePageMeta } from "@/hooks/usePageMeta";

interface ResultItem {
  id: string;
  type: "资讯" | "作品";
  title: string;
  desc: string;
  cover: string;
  meta: string;
  to: string;
  tags: string[];
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [type, setType] = useState<"全部" | "资讯" | "作品">("全部");
  const [sort, setSort] = useState<"relevance" | "latest">("relevance");

  usePageMeta(
    query ? `“${query}”的搜索结果 - 91动漫` : "搜索结果 - 91动漫",
    "在 91动漫搜索动漫资讯与作品推荐，支持按类型筛选与排序，快速找到你感兴趣的内容。",
    "91动漫,站内搜索,动漫搜索,动漫资讯,动漫作品"
  );

  useEffect(() => {
    setInput(query);
  }, [query]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as ResultItem[];

    const newsResults: ResultItem[] = newsList
      .filter((n) => {
        const hay = `${n.title}${n.summary}${n.tags.join("")}`.toLowerCase();
        return hay.includes(q);
      })
      .map((n) => ({
        id: n.id,
        type: "资讯" as const,
        title: n.title,
        desc: n.summary,
        cover: n.cover,
        meta: n.date,
        to: `/news/${n.id}`,
        tags: n.tags,
      }));

    const workResults: ResultItem[] = worksList
      .filter((w) => {
        const hay = `${w.title}${w.synopsis}${w.category}${w.region}${w.tags.join("")}`.toLowerCase();
        return hay.includes(q);
      })
      .map((w) => ({
        id: w.id,
        type: "作品" as const,
        title: w.title,
        desc: w.synopsis,
        cover: w.cover,
        meta: `${w.category} · ${w.region} · ${w.year}`,
        to: `/recommend/${w.id}`,
        tags: w.tags,
      }));

    let all = [...newsResults, ...workResults];
    if (type === "资讯") all = newsResults;
    if (type === "作品") all = workResults;
    if (sort === "latest") all = [...all].sort((a, b) => (a.meta < b.meta ? 1 : -1));
    return all;
  }, [query, type, sort]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setSearchParams({ q });
    else setSearchParams({});
  };

  const hotKeywords = ["星海航路", "国漫", "科幻", "治愈", "专题"];

  return (
    <div className="w-full">
      <PageHero
        title="搜索"
        description="在 91动漫全站搜索动漫资讯与作品，支持按类型筛选与排序，快速找到你想要的内容。"
        crumbs={[{ label: "搜索" }]}
        icon="ri-search-line"
      />

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <form
          onSubmit={submit}
          className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row sm:items-center md:p-5"
        >
          <div className="flex flex-1 items-center rounded-full border border-background-300/80 bg-background-100 px-4 py-2.5 transition-colors focus-within:border-accent-400">
            <i className="ri-search-line mr-2 text-lg text-foreground-500" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，如：星海航路 / 国漫 / 科幻"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-foreground-500">热门搜索：</span>
          {hotKeywords.map((k) => (
            <Link
              key={k}
              to={`/search?q=${encodeURIComponent(k)}`}
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
            >
              {k}
            </Link>
          ))}
        </div>

        {query ? (
          <>
            <div className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
              <p className="text-sm text-foreground-600">
                搜索“<strong className="text-foreground-950">{query}</strong>”共找到{" "}
                <strong className="text-primary-600">{results.length}</strong> 条结果
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
                  {(["全部", "资讯", "作品"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                        type === t ? "bg-accent-500 text-white" : "text-secondary-800 hover:bg-secondary-200"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
                  <button
                    type="button"
                    onClick={() => setSort("relevance")}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      sort === "relevance" ? "bg-primary-500 text-white" : "text-secondary-800 hover:bg-secondary-200"
                    }`}
                  >
                    相关度
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("latest")}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      sort === "latest" ? "bg-primary-500 text-white" : "text-secondary-800 hover:bg-secondary-200"
                    }`}
                  >
                    最新
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-4">
              {results.length > 0 ? (
                results.map((r) => (
                  <Link
                    key={`${r.type}-${r.id}`}
                    to={r.to}
                    className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:flex-row md:p-4"
                  >
                    <div className="h-40 w-full shrink-0 overflow-hidden rounded-md sm:h-28 sm:w-44">
                      <img
                        src={r.cover}
                        alt={`${r.title} - 91动漫`}
                        title={`${r.title} - 91动漫`}
                        className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center gap-2">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            r.type === "资讯"
                              ? "bg-primary-100 text-primary-800"
                              : "bg-accent-100 text-accent-800"
                          }`}
                        >
                          {r.type}
                        </span>
                        <span className="text-xs text-foreground-400">{r.meta}</span>
                      </div>
                      <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                        {r.title}
                      </h3>
                      <p className="line-clamp-2 mt-1.5 text-sm leading-relaxed text-foreground-500">{r.desc}</p>
                    </div>
                  </Link>
                ))
              ) : (
                <EmptyState
                  title="没有找到相关内容"
                  description="换一个关键词试试，或者从下面的热门推荐开始浏览。"
                  actionLabel="清空搜索"
                  onAction={() => {
                    setSearchParams({});
                  }}
                  icon="ri-emotion-unhappy-line"
                />
              )}
            </div>

            {results.length === 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-lg font-bold text-foreground-950">热门内容推荐</h3>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {newsList.slice(0, 3).map((n) => (
                    <Link
                      key={n.id}
                      to={`/news/${n.id}`}
                      className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                    >
                      <div className="h-36 w-full overflow-hidden">
                        <img
                          src={n.cover}
                          alt={`${n.title} - 91动漫`}
                          title={`${n.title} - 91动漫`}
                          className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <h4 className="line-clamp-2 p-3 text-sm font-medium text-foreground-800 group-hover:text-primary-700">
                        {n.title}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="mt-6">
            <EmptyState
              title="输入关键词开始搜索"
              description="试试搜索作品名、动漫类型或资讯标签，例如“星海航路”“国漫”“科幻”。"
              icon="ri-search-eye-line"
            />
          </div>
        )}
      </section>
    </div>
  );
}